import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { CustomFieldOption, TicketSystemFieldsKey } from '../model/types';

export default function useConditionOptions(
    { actions, triggers }: { actions?: boolean; triggers?: boolean } = { actions: false, triggers: false }
) {
    const { t } = useTranslation('admin');

    const localizeOptions = useCallback(
        (key: TicketSystemFieldsKey, options: CustomFieldOption[] = []) => {
            if (!key) return options ?? [];

            if (!key.startsWith('ts.')) {
                key = `ts.${key.toLowerCase()}` as TicketSystemFieldsKey;
            }

            return options?.map((option) => {
                const { label, ...rest } = option;

                const transKey = `conditionOptions.${key}.${label}`;
                const transLabel = t(transKey as any);

                return {
                    label: transKey === transLabel ? label : transLabel,
                    ...rest
                };
            });
        },
        [t]
    );

    const localizeCheckBoxOptions = useCallback(
        (options: CustomFieldOption[] = []) => {
            return options?.map((option) => {
                const { label, value, ...rest } = option;

                return {
                    label: t(`checkboxOptions.${label}` as any),
                    value,
                    ...rest
                };
            });
        },
        [t]
    );

    const localizeFieldOptions = useCallback(
        (options: (Pick<CustomFieldOption, 'label'> & { value: string })[] = []) => {
            return options.map((option) => {
                let optionValueRef = option.value;
                if (optionValueRef && !optionValueRef.startsWith('ts.')) {
                    optionValueRef = `ts.${optionValueRef}` as TicketSystemFieldsKey;
                }
                const transKey = actions
                    ? `actionFieldOptions.${optionValueRef.toLowerCase()}`
                    : `conditionFieldOptions.${triggers ? optionValueRef.toLowerCase() : optionValueRef}`;
                const transLabel = t(transKey as any);

                return {
                    label: transKey === transLabel ? option.label : transLabel,
                    value: option.value
                };
            });
        },
        [actions, triggers, t]
    );

    const localizeOperators = useCallback(
        (options: (Pick<CustomFieldOption, 'label'> & { value: string })[] = []) =>
            options.map((option) => {
                const transKey = `conditionOperators.${option.value}`;
                const transLabel = t(transKey as any);

                return {
                    label: transKey === transLabel ? option.label : transLabel,
                    value: option.value,
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    disabled: option?.disabled
                };
            }),

        [t]
    );
    const localizeUserTypeActionOptions = useCallback(
        (options: CustomFieldOption[] = []) => {
            return options?.map((option) => {
                const { label, value, ...rest } = option;

                const transKey = `conditionUserTypeOptions.${label}`;
                const transLabel = t(transKey as any);

                return {
                    label: transKey === transLabel ? label : transLabel,
                    value,
                    ...rest
                };
            });
        },
        [t]
    );

    return {
        localizeFieldOptions,
        localizeOperators,
        localizeOptions,
        localizeCheckBoxOptions,
        localizeUserTypeActionOptions
    };
}
